import { useTranslation } from 'react-i18next';
import { useDeleteGlossary } from '@utils/api/glossaries/use-delete-glossary.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Dispatch, SetStateAction } from 'react';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import toast from 'react-hot-toast';
import { Glossary } from '@utils/types/glossary';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type Props = {
  glossary: Glossary | null;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  onDeleteSuccess?: () => void;
};

export default function DeleteGlossaryModal({
  glossary,
  isModalOpen,
  setIsModalOpen,
  onDeleteSuccess
}: Props) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';
  const dangerBg = isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)';
  const dangerBorder = 'rgba(239,68,68,0.25)';
  const dangerText = isDark ? 'rgb(252,165,165)' : 'rgb(185,28,28)';
  const { mutate: deleteGlossary, isLoading } = useDeleteGlossary();

  const handleDelete = () => {
    if (!glossary) return;

    deleteGlossary(glossary.id, {
      onSuccess: () => {
        toast.success(t('glossary-deleted-successfully'));
        setIsModalOpen(false);
        onDeleteSuccess?.();
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error?.response?.data.detail ||
          error?.response?.data.error ||
          error?.response?.data.message ||
          t('an-error-occurred-while-deleting-the-glossary');

        toast.error(errorMessage);
      }
    });
  };

  if (!glossary) return null;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      size="small"
      title={t('delete-glossary')}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => setIsModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />,
        <Button
          key="delete"
          onClick={handleDelete}
          label={t('delete')}
          size="small"
          variant="danger"
          disabled={isLoading}
        />
      ]}
    >
      <div className="space-y-4">
        <p className="text-sm" style={{ color: getColour('normalText') }}>
          {t('delete-glossary-confirmation', { name: glossary.name })}
        </p>

        <div
          className="p-3 rounded-2xl space-y-2"
          style={{
            backgroundColor: getColour('appSoft'),
            border: `1px solid ${getColour('hairline')}`
          }}
        >
          <div className="flex justify-between text-sm">
            <span
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('name')}:
            </span>
            <span style={{ color: getColour('normalText') }}>
              {glossary.name}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('language-pair')}:
            </span>
            <span
              className="font-mono"
              style={{ color: getColour('normalText') }}
            >
              {glossary.source_language} → {glossary.target_language}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span
              className="font-medium"
              style={{ color: getColour('inactiveText') }}
            >
              {t('entry-count')}:
            </span>
            <span style={{ color: getColour('normalText') }}>
              {glossary.entry_count}
            </span>
          </div>
        </div>

        <div
          className="p-3 rounded-2xl"
          style={{
            backgroundColor: dangerBg,
            border: `1px solid ${dangerBorder}`
          }}
        >
          <p className="text-sm" style={{ color: dangerText }}>
            {t('glossary-delete-warning')}
          </p>
        </div>
      </div>
    </Modal>
  );
}
